import { create } from 'zustand'
import { assistantApi } from '@/lib/api'

interface Message {
  id: string
  content: string
  isUser: boolean
  timestamp: string
  imageUrl?: string
  pending?: boolean
  streaming?: boolean
  feedback?: 'like' | 'dislike' | null
}

interface Folder {
  id: string
  name: string
  color: string
  createdAt: string
  conversationCount: number
}

interface Conversation {
  id: string
  title: string
  messages: Message[]
  createdAt: string
  updatedAt: string
  folderId: string | null
}

interface ConversationsState {
  conversations: Conversation[]
  folders: Folder[]
  currentConversationId: string | null
  loading: boolean

  // Conversation actions
  loadConversations: () => Promise<void>
  createConversation: () => Promise<string>
  createLocalConversation: () => string
  setCurrentConversation: (id: string | null) => void
  deleteConversation: (id: string) => Promise<void>
  updateConversationTitle: (id: string, title: string) => Promise<void>
  addMessage: (conversationId: string, content: string, isUser: boolean) => Promise<void>
  addMessageOptimistic: (conversationId: string, content: string, isUser: boolean, imageUrl?: string, pending?: boolean, streaming?: boolean) => string
  commitMessage: (conversationId: string, tempId: string, dbContent: string, isUser: boolean, imageUrl?: string) => Promise<void>
  updateStreamingMessage: (conversationId: string, tempId: string, content: string) => void
  removeOptimisticMessage: (conversationId: string, tempId: string) => void
  moveToFolder: (conversationId: string, folderId: string | null) => Promise<void>
  setMessageFeedback: (conversationId: string, messageId: string, feedback: 'like' | 'dislike' | null) => Promise<void>
  resetConversations: () => void

  // Folder actions
  loadFolders: () => Promise<void>
  createFolder: (name: string, color?: string) => Promise<Folder>
  updateFolder: (id: string, name?: string, color?: string) => Promise<void>
  deleteFolder: (id: string) => Promise<void>

  // Getters
  getCurrentConversation: () => Conversation | null
  getConversationById: (id: string) => Conversation | null
}

export const useConversationsStore = create<ConversationsState>()((set, get) => ({
  conversations: [],
  folders: [],
  currentConversationId: null,
  loading: false,

  loadConversations: async () => {
    try {
      set({ loading: true })
      const response = await assistantApi.get('/assistant/conversations')
      const conversations = response.data.map((conv: any) => ({
        id: conv.id,
        title: conv.title,
        messages: conv.messages.map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          isUser: msg.is_user,
          timestamp: msg.timestamp,
          feedback: msg.feedback_rating === 5 ? 'like' : msg.feedback_rating === 1 ? 'dislike' : null,
        })),
        createdAt: conv.created_at,
        updatedAt: conv.updated_at,
        folderId: conv.folder_id ?? null
      }))
      set({ conversations, loading: false })
    } catch (error) {
      console.error('Error loading conversations:', error)
      set({ loading: false })
    }
  },

  createLocalConversation: () => {
    const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const localConv: Conversation = {
      id: localId,
      title: 'Nova conversa',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      folderId: null,
    }
    set(state => ({
      conversations: [localConv, ...state.conversations],
      currentConversationId: localId,
    }))
    return localId
  },

  createConversation: async () => {
    try {
      const response = await assistantApi.post('/assistant/conversations', {
        title: 'Nova conversa'
      })
      const newConversation: Conversation = {
        id: response.data.id,
        title: response.data.title,
        messages: [],
        createdAt: response.data.created_at,
        updatedAt: response.data.updated_at,
        folderId: null
      }
      set(state => ({
        conversations: [newConversation, ...state.conversations],
        currentConversationId: newConversation.id
      }))
      return newConversation.id
    } catch (error) {
      console.error('Error creating conversation:', error)
      throw error
    }
  },

  deleteConversation: async (id: string) => {
    try {
      await assistantApi.delete(`/assistant/conversations/${id}`)
      const state = get()
      const newConversations = state.conversations.filter(c => c.id !== id)
      const newCurrentId = state.currentConversationId === id 
        ? (newConversations.length > 0 ? newConversations[0].id : null)
        : state.currentConversationId
      set({
        conversations: newConversations,
        currentConversationId: newCurrentId
      })
    } catch (error) {
      console.error('Error deleting conversation:', error)
    }
  },

  setCurrentConversation: (id: string | null) => {
    set({ currentConversationId: id })
  },

  updateConversationTitle: async (id: string, title: string) => {
    try {
      await assistantApi.put(`/assistant/conversations/${id}`, { title })
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === id 
            ? { ...c, title, updatedAt: new Date().toISOString() }
            : c
        )
      }))
    } catch (error) {
      console.error('Error updating conversation title:', error)
    }
  },

  addMessageOptimistic: (conversationId: string, content: string, isUser: boolean, imageUrl?: string, pending?: boolean, streaming?: boolean) => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId
          ? {
              ...c,
              messages: [...c.messages, { id: tempId, content, isUser, timestamp: new Date().toISOString(), imageUrl, pending, streaming }],
              updatedAt: new Date().toISOString()
            }
          : c
      )
    }))
    return tempId
  },

  updateStreamingMessage: (conversationId: string, tempId: string, content: string) => {
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId
          ? {
              ...c,
              messages: c.messages.map(m =>
                m.id === tempId ? { ...m, content, streaming: true } : m
              ),
            }
          : c
      )
    }))
  },

  removeOptimisticMessage: (conversationId: string, tempId: string) => {
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId
          ? { ...c, messages: c.messages.filter(m => m.id !== tempId) }
          : c
      )
    }))
  },

  commitMessage: async (conversationId: string, tempId: string, dbContent: string, isUser: boolean, imageUrl?: string) => {
    try {
      const response = await assistantApi.post(
        `/assistant/conversations/${conversationId}/messages`,
        { content: dbContent, is_user: isUser }
      )
      const realMessage: Message = {
        id: response.data.id,
        content: response.data.content,
        isUser: response.data.is_user,
        timestamp: response.data.timestamp,
        imageUrl,
      }
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === conversationId
            ? {
                ...c,
                messages: c.messages.map(m => m.id === tempId ? realMessage : m),
                updatedAt: new Date().toISOString()
              }
            : c
        )
      }))

      // Auto-update title if it's the first user message
      const conv = get().conversations.find(c => c.id === conversationId)
      if (conv && conv.title === 'Nova conversa' && isUser && conv.messages.filter(m => m.isUser).length === 1) {
        try {
          const titleResponse = await assistantApi.post('/assistant/generate-title', { message: dbContent })
          await get().updateConversationTitle(conversationId, titleResponse.data.title)
        } catch {
          await get().updateConversationTitle(conversationId, dbContent.slice(0, 30) + (dbContent.length > 30 ? '...' : ''))
        }
      }
    } catch (error) {
      console.error('Error committing message:', error)
      throw error
    }
  },

  addMessage: async (conversationId: string, content: string, isUser: boolean) => {
    try {
      const response = await assistantApi.post(
        `/assistant/conversations/${conversationId}/messages`,
        { content, is_user: isUser }
      )
      const newMessage: Message = {
        id: response.data.id,
        content: response.data.content,
        isUser: response.data.is_user,
        timestamp: response.data.timestamp
      }
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === conversationId
            ? {
                ...c,
                messages: [...c.messages, newMessage],
                updatedAt: new Date().toISOString()
              }
            : c
        )
      }))
      
      // Auto-update title if it's the first user message
      const conv = get().conversations.find(c => c.id === conversationId)
      if (conv && conv.title === 'Nova conversa' && isUser && conv.messages.length === 1) {
        try {
          // Generate intelligent title using LLM
          const response = await assistantApi.post('/assistant/generate-title', {
            message: content
          })
          const intelligentTitle = response.data.title
          await get().updateConversationTitle(conversationId, intelligentTitle)
        } catch (error) {
          console.error('Error generating intelligent title:', error)
          // Fallback to simple truncation
          await get().updateConversationTitle(conversationId, content.slice(0, 30) + (content.length > 30 ? '...' : ''))
        }
      }
    } catch (error) {
      console.error('Error adding message:', error)
      throw error
    }
  },

  getMessages: (conversationId: string) => {
    const conversation = get().conversations.find(c => c.id === conversationId)
    return conversation?.messages || []
  },

  getCurrentConversation: () => {
    const state = get()
    return state.conversations.find(c => c.id === state.currentConversationId) || null
  },

  getConversationById: (id: string) => {
    return get().conversations.find(c => c.id === id) || null
  },

  moveToFolder: async (conversationId: string, folderId: string | null) => {
    try {
      await assistantApi.patch(`/assistant/conversations/${conversationId}/folder`, { folder_id: folderId })
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === conversationId ? { ...c, folderId } : c
        )
      }))
    } catch (error) {
      console.error('Error moving conversation to folder:', error)
    }
  },

  setMessageFeedback: async (conversationId: string, messageId: string, feedback: 'like' | 'dislike' | null) => {
    // Optimistic update
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId
          ? { ...c, messages: c.messages.map(m => m.id === messageId ? { ...m, feedback } : m) }
          : c
      )
    }))
    try {
      const rating = feedback === 'like' ? 5 : 1
      await assistantApi.post(`/assistant/conversations/messages/${messageId}/feedback`, { rating })
    } catch (error) {
      console.error('Error saving message feedback:', error)
      // Revert optimistic update on error
      set(state => ({
        conversations: state.conversations.map(c =>
          c.id === conversationId
            ? { ...c, messages: c.messages.map(m => m.id === messageId ? { ...m, feedback: null } : m) }
            : c
        )
      }))
    }
  },

  loadFolders: async () => {
    try {
      const response = await assistantApi.get('/assistant/folders')
      const folders: Folder[] = response.data.map((f: any) => ({
        id: f.id,
        name: f.name,
        color: f.color,
        createdAt: f.created_at,
        conversationCount: f.conversation_count
      }))
      set({ folders })
    } catch (error) {
      console.error('Error loading folders:', error)
    }
  },

  createFolder: async (name: string, color = '#a3de5d') => {
    const response = await assistantApi.post('/assistant/folders', { name, color })
    const folder: Folder = {
      id: response.data.id,
      name: response.data.name,
      color: response.data.color,
      createdAt: response.data.created_at,
      conversationCount: 0
    }
    set(state => ({ folders: [...state.folders, folder] }))
    return folder
  },

  updateFolder: async (id: string, name?: string, color?: string) => {
    const body: any = {}
    if (name !== undefined) body.name = name
    if (color !== undefined) body.color = color
    const response = await assistantApi.put(`/assistant/folders/${id}`, body)
    set(state => ({
      folders: state.folders.map(f =>
        f.id === id ? { ...f, name: response.data.name, color: response.data.color } : f
      )
    }))
  },

  deleteFolder: async (id: string) => {
    await assistantApi.delete(`/assistant/folders/${id}`)
    set(state => ({
      folders: state.folders.filter(f => f.id !== id),
      conversations: state.conversations.map(c =>
        c.folderId === id ? { ...c, folderId: null } : c
      )
    }))
  },

  resetConversations: () => {
    set({
      conversations: [],
      folders: [],
      currentConversationId: null,
      loading: false
    })
  }
}))